import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import { useMapEnginePersistence } from './useMapEnginePersistence';
import { MapEngine } from '../types';

const DEFAULT_FALLBACK_MS = 5000;
const DEFAULT_PERSIST_TTL_MS = 60 * 60 * 1000;

interface UseMapEngineFallbackOptions {
    persistKey?: string;
    persistTtlMs?: number;
    fallbackTimeoutMs?: number;
    initialEngine?: MapEngine;
}

interface UseMapEngineFallbackResult {
    engine: MapEngine;
    googleFailed: boolean;
    reportLoaded: () => void;
    reportError: (reason: string) => void;
    retry: () => void;
}

export const useMapEngineFallback = ({
    persistKey,
    persistTtlMs = DEFAULT_PERSIST_TTL_MS,
    fallbackTimeoutMs = DEFAULT_FALLBACK_MS,
    initialEngine,
}: UseMapEngineFallbackOptions): UseMapEngineFallbackResult => {
    const persistence = useMapEnginePersistence({ persistKey, persistTtlMs });

    const [engine, setEngine] = useState<MapEngine>(() => {
        const persisted = persistence.read();

        if (persisted) return persisted;

        return initialEngine ?? 'mapbox';
    });

    const [googleFailed, setGoogleFailed] = useState(false);
    const timerRef = useRef<number | null>(null);

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (engine !== 'mapbox') return undefined;

        timerRef.current = window.setTimeout(() => {
            persistence.write('google');
            setEngine('google');
        }, fallbackTimeoutMs);

        return () => clearTimer();
    }, [engine, fallbackTimeoutMs, persistence, clearTimer]);

    const reportLoaded = useCallback(() => {
        clearTimer();
    }, [clearTimer]);

    const reportError = useCallback((_reason: string) => {
        clearTimer();
        setEngine((prev) => {
            if (prev === 'mapbox') {
                persistence.write('google');

                return 'google';
            }

            setGoogleFailed(true);

            return prev;
        });
    }, [clearTimer, persistence]);

    const retry = useCallback(() => {
        persistence.clear();
        setGoogleFailed(false);
        setEngine('mapbox');
    }, [persistence]);

    return {
        engine, googleFailed, reportLoaded, reportError, retry,
    };
};
